// src/services/inventoryInstancesAPI.ts
// Inventory Instances API service for qmLEO
// Handles inventory instances (serialized items and bulk stock)
// Calls /inventory-instances/* endpoints

import { authService } from './authService';
import { logger } from '../utils/logger';
import { getApiBaseUrl } from './configService';

const instancesLogger = logger.createServiceLogger('InventoryInstancesAPI');

// Types
export interface InventoryInstance {
  tenantId: string;
  inventoryId: string;
  productId: string;
  itemType: 'SERIALIZED' | 'BULK';
  
  // Serialized fields
  serialNumber?: string;
  status?: 'AVAILABLE' | 'ASSIGNED' | 'MAINTENANCE' | 'RETIRED';
  condition?: string;
  assignedTo?: string;
  assignedToName?: string;
  assignmentDate?: string;
  
  // Bulk fields
  totalQuantity?: number;
  availableQuantity?: number;
  committedQuantity?: number;
  minStockLevel?: number;
  reorderPoint?: number;
  lotNumber?: string;
  expirationDate?: string;
  
  // Common fields
  location: string;
  subLocation?: string;
  photos?: string[];
  notes?: string;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
}

export interface BulkStock extends InventoryInstance {
  itemType: 'BULK';
  totalQuantity: number;
  availableQuantity: number;
  committedQuantity: number;
  quantityAdjustments?: Array<{
    date: string;
    previousQuantity: number;
    newQuantity: number;
    adjustmentType: string;
    reason: string;
    performedBy: string;
  }>;
}

// API response wrapper
interface APIResponse<T> {
  success: boolean;
  data?: T;
  count?: number;
  error?: {
    code: string;
    message: string;
  };
}

class InventoryInstancesAPI {
  private baseUrl: string;

  constructor() {
    this.baseUrl = getApiBaseUrl();
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const endTimer = logger.startTimer('InventoryInstancesAPI', `${options.method || 'GET'} ${endpoint}`);
    
    logger.logAPIRequest('InventoryInstancesAPI', options.method || 'GET', `${this.baseUrl}${endpoint}`, options.body);

    const headers = new Headers(options.headers || {});
    if (options.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }

    try {
      const response = await authService.makeAuthenticatedRequest(
        `${this.baseUrl}${endpoint}`,
        { ...options, headers }
      );

      logger.logAPIResponse('InventoryInstancesAPI', options.method || 'GET', endpoint, response.status);
      endTimer();

      const data = await response.json();

      if (!response.ok) {
        instancesLogger.error(`Request failed with status ${response.status}`, { 
          endpoint, 
          status: response.status, 
          data 
        });
        return {
          success: false,
          error: data.error || {
            code: 'HTTP_ERROR',
            message: `HTTP ${response.status}`,
          },
        };
      }

      return data as APIResponse<T>;
    } catch (error: any) {
      endTimer();
      instancesLogger.error('Request failed', { endpoint, method: options.method }, error);

      if (error.code === 'NO_SESSION') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Please log in again' },
        };
      }

      if (error.code === 'TOKEN_EXPIRED') {
        return {
          success: false,
          error: { code: 'AUTH_ERROR', message: 'Session expired, please refresh' },
        };
      }

      return {
        success: false,
        error: { code: 'REQUEST_ERROR', message: error.message || 'Request failed' },
      };
    }
  }

  /**
   * Get all inventory instances for a product
   * Optionally filter by itemType (SERIALIZED or BULK)
   */
  async getByProductId(productId: string, itemType?: 'SERIALIZED' | 'BULK'): Promise<InventoryInstance[]> {
    instancesLogger.debug('Fetching instances by productId', { productId, itemType });

    const encodedProductId = encodeURIComponent(productId);
    const endpoint = `/inventory-instances?productId=${encodedProductId}`;
    
    const response = await this.makeRequest<InventoryInstance[]>(endpoint);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch inventory instances');
    }

    let instances = response.data || [];
    
    // Filter by itemType if specified (backend may not support this filter)
    if (itemType) {
      instances = instances.filter(i => i.itemType === itemType);
    }

    instancesLogger.info('Instances fetched', { productId, count: instances.length, itemType });
    return instances;
  }

  /**
   * Get existing bulk stock for a product
   * Returns the first bulk stock found, or null if none exists
   * UPDATED: Now calls /fulfillment/check-stock which queries qm_inventory_items_dev
   */
  async getExistingBulkStock(productId: string): Promise<BulkStock | null> {
    instancesLogger.debug('Checking for existing bulk stock', { productId });

    const encodedProductId = encodeURIComponent(productId);
    const response = await this.makeRequest<{ exists: boolean; stock: BulkStock | null }>(
      `/fulfillment/check-stock?productId=${encodedProductId}`
    );

    if (!response.success) {
      instancesLogger.error('Failed to check for existing bulk stock', { productId, error: response.error });
      return null;
    }

    if (!response.data?.exists || !response.data?.stock) {
      instancesLogger.info('No existing bulk stock found', { productId });
      return null;
    }

    // Map itemId to inventoryId for compatibility with existing code
    const stock = response.data.stock as any;
    const bulkStock = {
      ...stock,
      inventoryId: stock.itemId || stock.inventoryId
    } as BulkStock;
    
    instancesLogger.info('Existing bulk stock found', { 
      productId, 
      stockId: bulkStock.inventoryId,
      currentQuantity: bulkStock.totalQuantity 
    });
    return bulkStock;
  }

  /**
   * Get a single inventory instance by ID
   */
  async getById(inventoryId: string): Promise<InventoryInstance> {
    instancesLogger.debug('Fetching instance by ID', { inventoryId });

    const encodedId = encodeURIComponent(inventoryId);
    const response = await this.makeRequest<InventoryInstance>(`/inventory-instances/${encodedId}`);

    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Inventory instance not found');
    }

    instancesLogger.info('Instance fetched', { inventoryId });
    return response.data;
  }

  /**
   * Get all inventory instances (with optional filters)
   */
  async getAll(filters?: { 
    status?: string; 
    assignedTo?: string;
    location?: string;
    itemType?: 'SERIALIZED' | 'BULK';
  }): Promise<InventoryInstance[]> {
    instancesLogger.debug('Fetching all instances', filters);

    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.assignedTo) params.append('assignedTo', filters.assignedTo);
    
    const endpoint = `/inventory-instances${params.toString() ? `?${params}` : ''}`;
    const response = await this.makeRequest<InventoryInstance[]>(endpoint);

    if (!response.success) {
      throw new Error(response.error?.message || 'Failed to fetch inventory instances');
    }

    let instances = response.data || [];

    // Client-side filters
    if (filters?.location) {
      instances = instances.filter(i => i.location === filters.location);
    }
    if (filters?.itemType) {
      instances = instances.filter(i => i.itemType === filters.itemType);
    }

    instancesLogger.info('Instances fetched', { count: instances.length });
    return instances;
  }
}

// Export singleton instance
export const inventoryInstancesAPI = new InventoryInstancesAPI();
